"use client";

import { useTranslations } from "next-intl";
import { ArrowLeft, Trophy } from "lucide-react";
import { useParams } from "next/navigation";
import { Link } from "@/i18n/navigation";
import { useDelayedLoading } from "@/shared/lib/hooks/use-delayed-loading";
import { useVisibleInterval } from "@/shared/lib/hooks/use-visible-interval";
import { useEffect, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { useQuery } from "@tanstack/react-query";

import { useTournamentStore } from "@/entities/tournament";
import { api } from "@/shared/lib/api";
import { archiveKeys } from "@/shared/api/query-keys";
import { Button } from "@/shared/ui/buttons/button";
import { TournamentDetailHeader } from "@/widgets/tournament-detail/tournament-detail-header";
import { TournamentDetailPageSkeleton } from "@/widgets/tournament-detail/skeletons/tournament-detail-page-skeleton";
import { TournamentDetailTabs } from "@/widgets/tournament-detail/tournament-detail-tabs";
import { EmptyState } from "@/widgets/shared/empty-state";
import { DownloadAppBanner } from "@/widgets/tauri/download-app-banner";
import { TournamentArchiveResults } from "@/widgets/tournament-archives/tournament-archive-results";
import { formatDateOnlyMsk } from "@/shared/lib/msk-time";


/**
 * `serverFallback` — серверная сводка турнира (KAN-1225): её видно, пока клиент
 * не загрузил турнир, вместо пустого места или скелетона. Так содержимое есть
 * в HTML для поисковика, а пользователь сразу видит суть.
 *
 * `serverHint` — id и статус того же турнира, уже загруженного сервером
 * (KAN-993). По нему проба архива завершённого турнира стартует сразу при
 * гидратации, а не ждёт повторной загрузки турнира клиентом.
 */
export function TournamentPageClient({
  serverFallback,
  serverHint,
}: {
  serverFallback?: React.ReactNode;
  serverHint?: { id: string; status: string | null };
}) {
  const tUi = useTranslations("Ui");
  const tArchive = useTranslations("Tournaments.archivePage");
  const {
    currentTournament: tournament,
    isLoading,
    fetchTournamentById,
  } = useTournamentStore(
    useShallow((state) => ({
      currentTournament: state.currentTournament,
      isLoading: state.isLoading,
      fetchTournamentById: state.fetchTournamentById,
    })),
  );
  const params = useParams();
  const tournamentIdOrSlug = params?.["id"] as string | undefined;
  // KAN-202: архивная запись турнира (ручка отдаёт её вне сген. DTO).
  type TournamentArchive = {
    name?: string | null;
    endDate?: string | null;
    tournamentId?: string | null;
    type?: string | null;
  };
  // На первом рендере (до старта fetch) isLoading=false и tournament=null —
  // без этого флага на секунду мелькало «Турнир не найден» вместо загрузки.
  // initializing=true до завершения первой попытки загрузки (турнир + архив).
  // dev_task fix-tournament-page-not-found-flash.
  // Первая попытка (турнир + архив) ещё идёт — считаем по факту запросов,
  // а не отдельным флагом состояния.
  /** Первая попытка загрузить турнир завершилась (успешно или нет). */
  const [tournamentLoadAttempted, setTournamentLoadAttempted] = useState(false);

  // Стабильные ключи для эффекта-пробы архива: чтобы он НЕ перезапускался на
  // каждый 20-сек polling (который создаёт новый объект tournament), а только
  // когда реально меняется турнир (id) или его статус (завершился → архив).
  const tournamentDbId =
    tournament?.id ||
    (tournament as { tournamentId?: string } | null)?.tournamentId ||
    serverHint?.id ||
    tournamentIdOrSlug;
  const tournamentStatusKey = tournament?.status as string | undefined;
  const isFinishedTournament =
    tournamentStatusKey === "Completed" || tournamentStatusKey === "Cancelled";

  /**
   * KAN-935, решение владельца 03.09.2026: страховочный интервал вместо
   * частого опроса. Детальная ручка турнира — самая дорогая на платформе
   * (3 030 с обработки за один вечер, 20% всего API-трафика), и 20 секунд для
   * данных, которые меняются несколько раз за турнир, это опрос ради опроса.
   * Совсем убирать нельзя: событий об изменении настроек турнира бэкенд не
   * шлёт вовсе, а KAN-919 показал, что и существующие теряются молча.
   */
  const TOURNAMENT_SAFETY_REFETCH_MS = 150_000;

  // Проба архива живёт в React Query: ключ один на весь фронт (archiveKeys),
  // поэтому два потребителя больше не уходят двумя параллельными запросами на
  // один URL — на проде это было видно замером (старт 1315 и 1317 мс).
  //
  // KAN-935: у живого турнира архива не существует — фоновая служба создаёт его
  // ровно один раз, через сутки после завершения. Спрашиваем только когда архив
  // в принципе возможен: турнир завершён/отменён, либо не нашёлся вовсе, но
  // попытка загрузки уже была (до неё tournamentDbId — это slug из URL, а ручка
  // работает по id, то есть гарантированный 404).
  //
  // 404 — окончательный ответ «архива пока нет», поэтому staleTime бесконечный
  // и повторов нет: раньше эту память держал useRef, теперь её держит кэш.
  //
  // TODO(perf, push-based): правильнее получать появление архива событием,
  //   а не пробой. См. TODO у polling-эффекта выше.
  // KAN-993: сервер уже знает id и статус турнира. Если он завершён, проба
  // архива идёт сразу, параллельно с загрузкой турнира клиентом — замер на
  // проде: она стартовала на 120 мс позже, а за ней ждала вся страница итогов.
  // По id, а не по slug из URL: ручка архива работает только по id.
  const serverSaysFinished =
    serverHint?.status === "Completed" || serverHint?.status === "Cancelled";
  const archivePossible = tournament
    ? isFinishedTournament
    : serverSaysFinished || tournamentLoadAttempted;
  const archiveQueryEnabled =
    (!isLoading || serverSaysFinished) && Boolean(tournamentDbId) && archivePossible;

  const { data: archive = null, isFetched: archiveFetched } =
    useQuery<TournamentArchive | null>({
      queryKey: archiveKeys.detail(String(tournamentDbId ?? "")),
      queryFn: async () => {
        try {
          const res = await api.get(
            `/tournament-archives/${encodeURIComponent(String(tournamentDbId))}`,
          );
          return (res.data ?? null) as TournamentArchive | null;
        } catch {
          // «Архива нет» — это ответ, а не сбой: кладём null в кэш.
          return null;
        }
      },
      enabled: archiveQueryEnabled,
      staleTime: Infinity,
      retry: false,
    });

  const archiveData = archive;
  const isArchiveLoading = archiveQueryEnabled && !archiveFetched;
  // Первая попытка завершена, когда турнир уже спрашивали и проба архива либо
  // отработала, либо не нужна.
  const initializing = !tournamentLoadAttempted || isArchiveLoading;

  // KAN-306: полный скелетон страницы через delay-in + min-out — мгновенные
  // ответы (кэш/Postgres) не мелькают скелетоном, показанный держится ≥350мс.
  const detailPending =
    isLoading ||
    ((isArchiveLoading || initializing) &&
      (!tournament || isFinishedTournament));
  const showDetailSkeleton = useDelayedLoading(detailPending);
  const skeletonWasShownRef = useRef(false);
  if (showDetailSkeleton) skeletonWasShownRef.current = true;

  useEffect(() => {
    if (!tournamentIdOrSlug) return;
    setTournamentLoadAttempted(false);
    // KAN-935: проба архива ждёт исхода этой попытки. До неё `tournamentDbId` —
    // это slug из URL, а ручка архива работает по id: замер на проде показал
    // `/tournament-archives/august-draft-mode` → 404, а следом
    // `/tournament-archives/{uuid}` → 200. Один гарантированно бесполезный
    // запрос на КАЖДОЕ открытие любой страницы турнира.
    void Promise.resolve(fetchTournamentById(tournamentIdOrSlug)).finally(
      () => {
        setTournamentLoadAttempted(true);
      },
    );
  }, [tournamentIdOrSlug, fetchTournamentById]);

  // Страховочное обновление турнира: настройки и статус меняются организатором,
  // и ни один хаб об этом не сообщает (проверено по бэкенду: SendToTournamentAsync
  // зовётся только на генерацию сетки, отмену, этап таймлайна, авто-завершение и
  // check-in, и это персональные уведомления, а не broadcast). Поэтому опрос
  // остаётся, но редким.
  //
  // KAN-935: было 20 секунд — 22 489 запросов и 50 минут процессорного времени
  // бэкенда за один вечер турнира, 20% всего API-трафика. Стало 2,5 минуты плюс
  // гейт по видимости через общий хук: в свёрнутой вкладке запросов нет вовсе,
  // при возврате данные догоняются сразу, а не через интервал.
  const shouldPollTournament =
    Boolean(tournamentIdOrSlug) && !isFinishedTournament && !archive;
  useVisibleInterval(
    () => {
      if (tournamentIdOrSlug) fetchTournamentById(tournamentIdOrSlug);
    },
    shouldPollTournament ? TOURNAMENT_SAFETY_REFETCH_MS : null,
  );

  // Редирект на slug если загружен по UUID
  useEffect(() => {
    if (!isLoading && tournament && tournamentIdOrSlug) {
      const isUuid =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          tournamentIdOrSlug,
        );
      const tournamentSlug = tournament?.slug;
      if (isUuid && tournamentSlug) {
        window.history.replaceState({}, "", `/tournaments/${tournamentSlug}`);
      }
    }
  }, [tournament, isLoading, tournamentIdOrSlug]);

  // Проба архива: определяет, показывать живой вид или архивный.
  //

  // Только до первой загрузки: на фоновом переопросе полный интерфейс не
  // должен смениться сводкой.
  if (detailPending && serverFallback && !tournament && !archive) {
    return <div className="min-h-screen bg-background">{serverFallback}</div>;
  }
  if (showDetailSkeleton) {
    return (
      <div className="min-h-screen bg-background">
        <TournamentDetailPageSkeleton />
      </div>
    );
  }
  // Загрузка идёт, но delay-in ещё не истёк — держим фон без скелетона,
  // чтобы мгновенные ответы не мелькали.
  if (detailPending) {
    return <div className="min-h-screen bg-background" />;
  }

  // Пока идёт проба архива, мы ещё не знаем, архивный это турнир или живой.
  // Для завершённого/отменённого (или ещё не загруженного) турнира НЕЛЬЗЯ
  // показывать живую детальную страницу — иначе её хедер + DownloadAppBanner
  // вспыхнут на долю секунды и тут же сменятся архивным видом (ветка `archive`).
  // Активные турниры архива не имеют → их рендерим сразу, без лишнего скелетона.
  // (isFinishedTournament вычислен выше — переиспользуем.)
  if (!tournament && !archive && !isArchiveLoading && !initializing) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8">
          <EmptyState
            title={tUi("pageClient.turnirNeNayden")}
            description={tUi("pageClient.turnirSUkazannymId")}
            icon={<Trophy className="h-12 w-12 text-muted-foreground" />}
            actionLabel={tUi("litPageClient.vernutsyaKTurniram")}
            onAction={() => {
              window.location.href = "/tournaments";
            }}
          />
        </div>
      </div>
    );
  }

  if (archive) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8 space-y-4 sm:space-y-6">
          <div className="flex items-center justify-between">
            <Button variant="ghost" asChild>
              <Link href="/tournaments" className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                {tArchive("backToTournaments")}
              </Link>
            </Button>
          </div>

          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {String(archiveData?.name ?? tArchive("unnamed"))}
            </h1>
            <div className="text-sm text-muted-foreground">
              {/* Дата форматируется по ТЕКУЩЕЙ локали. Жёстко зашитый "ru-RU"
                  выдавал русский формат даже на английской странице — ошибка тем
                  незаметнее, что дата всё равно выглядит датой. */}
              {tArchive("archivedOn", {
                date: archiveData?.endDate
                  ? formatDateOnlyMsk(new Date(String(archiveData.endDate)))
                  : "—",
              })}
            </div>
          </div>

          <TournamentArchiveResults
            tournamentId={String(archiveData?.tournamentId ?? "")}
            tournamentType={archiveData?.type ?? null}
          />
        </div>
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="min-h-screen bg-background">
        {serverFallback ?? <TournamentDetailPageSkeleton />}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div
        className={
          "container mx-auto px-3 sm:px-4 py-4 sm:py-8" +
          (skeletonWasShownRef.current
            ? " animate-in fade-in duration-300 motion-reduce:animate-none"
            : "")
        }
      >
        <TournamentDetailHeader tournament={tournament} />
        {tournament.status !== "Completed" &&
          tournament.status !== "Cancelled" && (
            <DownloadAppBanner className="mb-4" />
          )}
        <TournamentDetailTabs tournament={tournament} />
      </div>
    </div>
  );
}
